import { useCallback } from 'react'
import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import { isShapeGeometryGestureBlocked } from '@/lib/groups/element-edit-policy'
import { buildUnifiedSelectionShapeResizeCommitItems } from '@/lib/transforms/unified-selection-resize'
import {
  type MultiSelectResizeShapeSnap,
  computeShapesOnlyMultiSelectResizeFromPanelTargets,
} from '@/lib/transforms/multi-select-chrome-resize'
import { isShapeSelectable } from '../helpers/selection-scope'
import {
  buildMultiSelectShapeSnap,
  computeMultiSelectChrome,
  getMultiSelectableIds,
} from '../helpers/multi-select-geometry'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { EditScopeRefsApi } from './use-edit-scope-refs'
import type { ShapeUpdatesApi } from './use-shape-updates'
import type { SelectionPresentationApi } from './use-selection-presentation'

export function useMultiSelectChromeCommands(
  ctx: ShapeManagerStateApi & EditScopeRefsApi & ShapeUpdatesApi & SelectionPresentationApi
) {
  const {
    editScopeGroupIdRef,
    multiSelectChromeRef,
    multiSelectSelectionKeyRef,
    multiSelectSyncPrevShapesRef,
    panelMultiRotateBaselineRef,
    pendingRotateChromeBeforeRef,
    scheduleLiveSelectionGeometrySync,
    scheduleLiveWrapSync,
    selectedShapeIdsRef,
    setMultiSelectChrome,
    shapeBoundsRef,
    shapesById,
    shapesRef,
    updateShapeRef,
  } = ctx

  const resyncMultiSelectChromeFromShapes = useCallback(() => {
    const ids = selectedShapeIdsRef.current
    const list = shapesRef.current

    if (ids.length < 2) {
      multiSelectSyncPrevShapesRef.current = null
      setMultiSelectChrome((prev) => (prev !== null ? null : prev))

      return
    }

    const next = computeMultiSelectChrome(ids, list)

    if (!next) {
      return
    }

    setMultiSelectChrome(next)
    multiSelectSyncPrevShapesRef.current = buildMultiSelectShapeSnap(
      getMultiSelectableIds(ids, list),
      list
    )
  }, [multiSelectSyncPrevShapesRef, selectedShapeIdsRef, setMultiSelectChrome, shapesRef])

  const getMultiSelectChromeSnapshot = useCallback((): MultiSelectChrome | null => {
    if (selectedShapeIdsRef.current.length < 2) return null

    const c = multiSelectChromeRef.current

    if (!c) return null

    return { ...c }
  }, [multiSelectChromeRef, selectedShapeIdsRef])

  const restoreMultiSelectChrome = useCallback(
    (chrome: MultiSelectChrome) => {
      const ids = selectedShapeIdsRef.current
      const list = shapesRef.current

      if (ids.length < 2) {
        // History may still carry a multi-select chrome snapshot after selection collapsed to one
        // item (e.g. group-only). Leaving refs/state set would keep a phantom dashed box until the
        // user toggled selection — mirror the multi-select exit cleanup from the layout effect.
        multiSelectSelectionKeyRef.current = ''
        panelMultiRotateBaselineRef.current = null
        pendingRotateChromeBeforeRef.current = null
        multiSelectSyncPrevShapesRef.current = null
        multiSelectChromeRef.current = null
        setMultiSelectChrome((prev) => (prev !== null ? null : prev))

        return
      }

      // History restore replays a known selection state + chrome snapshot. Prime the current
      // selection key so the selection-change effect does not immediately clear this chrome
      // as "stale" before paint.
      multiSelectSelectionKeyRef.current = [...ids].sort().join('|')
      panelMultiRotateBaselineRef.current = null
      pendingRotateChromeBeforeRef.current = null
      multiSelectChromeRef.current = chrome
      setMultiSelectChrome(chrome)
      multiSelectSyncPrevShapesRef.current = buildMultiSelectShapeSnap(
        getMultiSelectableIds(ids, list),
        list
      )
    },
    [
      multiSelectChromeRef,
      multiSelectSelectionKeyRef,
      multiSelectSyncPrevShapesRef,
      panelMultiRotateBaselineRef,
      pendingRotateChromeBeforeRef,
      selectedShapeIdsRef,
      setMultiSelectChrome,
      shapesRef,
    ]
  )

  const consumePendingRotateChromeBefore = useCallback((): MultiSelectChrome | null => {
    const c = pendingRotateChromeBeforeRef.current

    pendingRotateChromeBeforeRef.current = null

    return c ? { ...c } : null
  }, [pendingRotateChromeBeforeRef])

  const applyMultiSelectPanelDimensionCommit = useCallback(
    (opts: {
      targetChromeWidth: number
      targetChromeHeight: number
      field: 'width' | 'height'
      dimensionsLinked: boolean
    }): BatchShapeTransformItem[] | null => {
      const ids = selectedShapeIdsRef.current

      if (ids.length < 2) return null

      const chrome = multiSelectChromeRef.current

      if (!chrome) return null

      const chromeBeforeCommit: MultiSelectChrome = { ...chrome }

      const list = shapesRef.current
      const selected = list.filter(
        (s) =>
          ids.includes(s.id) &&
          isShapeSelectable(s, editScopeGroupIdRef.current) &&
          !isShapeGeometryGestureBlocked(s, shapesById)
      )

      if (selected.length < 2) return null

      const shapeInitials: Record<string, MultiSelectResizeShapeSnap> = Object.fromEntries(
        selected.map((s) => [
          s.id,
          {
            x: s.x,
            y: s.y,
            width: s.width,
            height: s.height,
            rotation: s.rotation ?? 0,
          },
        ])
      )

      const boundsMap = shapeBoundsRef.current
      const boundsInitials: Record<string, ShapeBounds | undefined> = Object.fromEntries(
        selected.map((s) => [s.id, boundsMap[s.id]])
      )

      const computed = computeShapesOnlyMultiSelectResizeFromPanelTargets({
        chrome,
        shapeInitials,
        boundsInitials,
        targetChromeWidth: opts.targetChromeWidth,
        targetChromeHeight: opts.targetChromeHeight,
        field: opts.field,
        dimensionsLinked: opts.dimensionsLinked,
      })

      if (!computed) return null

      for (const [id, u] of Object.entries(computed.updates)) {
        updateShapeRef.current(id, u)
      }

      setMultiSelectChrome(computed.nextChrome)
      multiSelectChromeRef.current = computed.nextChrome
      scheduleLiveSelectionGeometrySync()
      scheduleLiveWrapSync()

      const batchItems = buildUnifiedSelectionShapeResizeCommitItems({
        initials: Object.fromEntries(
          Object.entries(shapeInitials).map(([id, snap]) => [
            id,
            {
              x: snap.x,
              y: snap.y,
              width: snap.width,
              height: snap.height,
              rotation: snap.rotation,
            },
          ])
        ),
        shapes: shapesRef.current,
        positionEpsilon: 1e-3,
        sizeEpsilon: 1e-3,
        rotationEpsilon: 1e-2,
        includeRotation: true,
      })

      if (batchItems.length > 0) {
        pendingRotateChromeBeforeRef.current = chromeBeforeCommit
      }

      return batchItems
    },
    [
      scheduleLiveSelectionGeometrySync,
      scheduleLiveWrapSync,
      shapesById,
      editScopeGroupIdRef,
      multiSelectChromeRef,
      pendingRotateChromeBeforeRef,
      selectedShapeIdsRef,
      setMultiSelectChrome,
      shapeBoundsRef,
      shapesRef,
      updateShapeRef,
    ]
  )

  return {
    resyncMultiSelectChromeFromShapes,
    getMultiSelectChromeSnapshot,
    restoreMultiSelectChrome,
    consumePendingRotateChromeBefore,
    applyMultiSelectPanelDimensionCommit,
  }
}

export type MultiSelectChromeCommandsApi = ReturnType<typeof useMultiSelectChromeCommands>
